import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, Flex, Input, Select, Table, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { api, unwrap, type ActivityEntry } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { RefreshButton } from "@/components/RefreshButton";
import { DateRangeFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";

const ENTITY_TYPES = ["tenant", "member", "invitation", "warehouse", "order", "product", "stock", "channel", "supplier", "purchase_order", "vendor_return", "sku_cost", "supplier_bill", "supplier_credit", "cash", "books", "cost_profile"] as const;
/** A value of the payload in words: lists and objects shortened, never "[object Object]". */
function show(v: unknown, depth = 0): string {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    const shown = v.slice(0, 3).map((x) => show(x, depth + 1));
    return shown.join(", ") + (v.length > 3 ? ` (+${v.length - 3})` : "");
  }
  if (typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null && x !== undefined && x !== "");
    if (depth > 1) return `{${entries.length}}`;
    return `(${entries.map(([k, x]) => `${k} ${show(x, depth + 1)}`).join(", ")})`;
  }
  return String(v);
}

/** Short, human summary of the `changes` payload: "role: a → b", "name: X". */
function summarize(changes: Record<string, unknown>, roleLabel: (r: string) => string): string {
  return Object.entries(changes)
    .map(([field, v]) => {
      if (v && typeof v === "object" && !Array.isArray(v) && "from" in v && "to" in v) {
        const { from, to } = v as { from: unknown; to: unknown };
        const fmt = (x: unknown) => (field === "role" && typeof x === "string" ? roleLabel(x) : show(x));
        return `${field}: ${fmt(from)} → ${fmt(to)}`;
      }
      return `${field}: ${field === "role" && typeof v === "string" ? roleLabel(v) : show(v)}`;
    })
    .join(" · ");
}

export function ActivityLog() {
  const { t } = useI18n();
  const { can } = useAuth();
  const scopeNote = can("logs.all") ? null : can("logs.warehouse") ? t("logs.scopeWarehouse") : t("logs.scopeOrders");
  const [entityType, setEntityType] = useUrlFilter<string>("entity", "");
  const [action, setAction] = useUrlFilter<string>("action", "");
  const deferredAction = useDeferredValue(action.trim());
  const range = useDateRange();
  const page = useListPage();
  const filters = { ...(entityType ? { entityType } : {}), ...(deferredAction ? { action: deferredAction } : {}), ...range.query };

  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("logs.title")}
      </Typography.Title>
      {scopeNote && <Alert type="info" showIcon title={scopeNote} style={{ marginBottom: 16 }} />}
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Select<string>
          value={entityType}
          onChange={setEntityType}
          style={{ width: 200 }}
          options={[{ value: "", label: t("logs.allEntities") }, ...ENTITY_TYPES.map((e) => ({ value: e, label: t(`logs.entity.${e}`) }))]}
        />
        <Input allowClear placeholder={t("logs.actionFilter")} value={action} onChange={(e) => setAction(e.target.value)} style={{ width: 220 }} />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <RefreshButton queryKeys={[["activity"]]} />
        <ActivityExport filters={filters} />
      </Flex>
      <ActivityTable filters={filters} paging={page} />
    </>
  );
}

function ActivityExport({ filters }: { filters: Record<string, string> }) {
  const { t } = useI18n();
  return (
    <ExportButton
      path="/v1/activity/export"
      query={filters}
      filename={t("logs.title")}
      columns={[
        { key: "occurredAt", label: t("logs.time"), kind: "datetime" },
        { key: "actorName", label: t("logs.actor") },
        { key: "action", label: t("logs.action") },
        { key: "entityType", label: t("logs.entityLabel"), values: Object.fromEntries(ENTITY_TYPES.map((e) => [e, t(`logs.entity.${e}`)])) },
        { key: "entityId", label: t("logs.entityId") },
        { key: "changes", label: t("logs.detail") },
      ]}
    />
  );
}

/**
 * The activity list. On the log page it pages through the URL; inside another
 * page (e.g. an SKU's Log tab, entityType/entityId) it pages on its own.
 */
export function ActivityTable({
  entityType,
  entityId,
  filters,
  paging,
}: {
  entityType?: string;
  entityId?: string;
  filters?: Record<string, string>;
  paging?: ReturnType<typeof useListPage>;
}) {
  const { t, tDynamic, formatDateTime, formatNumber } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const [local, setLocal] = useState({ page: 1, size: 20 });
  const query = { ...(entityType ? { entityType } : {}), ...(entityId ? { entityId } : {}), ...filters };
  const pageQuery = paging ? paging.query : { limit: local.size, ...(local.page > 1 ? { offset: (local.page - 1) * local.size } : {}) };

  const q = useQuery({
    queryKey: ["activity", query, pageQuery],
    queryFn: () => unwrap(api.GET("/v1/activity", { params: { query: { ...query, ...pageQuery } } })),
    placeholderData: keepPreviousData,
  });

  const roleLabel = (r: string) => tDynamic(`role.${r}`, "field.invalid");
  const pagination = paging
    ? paging.pagination(q.data)
    : {
        current: local.page,
        pageSize: local.size,
        total: q.data?.total ?? 0,
        showSizeChanger: false,
        showTotal: (n: number) => t("list.total", { n: formatNumber(n) }),
        onChange: (page: number) => setLocal((x) => ({ ...x, page })),
      };

  return (
    <Table<ActivityEntry>
      rowKey="id"
      loading={q.isFetching && !q.data}
      dataSource={q.data?.items ?? []}
      pagination={pagination}
      locale={{ emptyText: t("logs.empty") }}
      columns={[
        { title: t("logs.time"), dataIndex: "occurredAt", width: 200, render: (v: string) => formatDateTime(v, tz) },
        { title: t("logs.actor"), dataIndex: "actorName", width: 200 },
        { title: t("logs.action"), dataIndex: "action", width: 240, render: (a: string) => tDynamic(`activity.${a}`, "logs.action") },
        {
          title: t("logs.detail"),
          dataIndex: "changes",
          render: (c: Record<string, unknown>) => <Typography.Text type="secondary">{summarize(c, roleLabel)}</Typography.Text>,
        },
      ]}
    />
  );
}
